import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from '../services/reportsService';

export const TestReports: React.FC = () => {
  const [testResults, setTestResults] = useState<any[]>([]);

  // Test customer transaction report
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['testCustomerReport'],
    queryFn: async () => {
      console.log('🧪 Testing customer transaction report...');
      try {
        const result = await reportsService.getCustomerTransactionReport({});
        console.log('✅ Customer transaction report success:', result);
        setTestResults(prev => [...prev, {
          test: 'Customer Transaction Report',
          status: 'SUCCESS',
          data: result,
          timestamp: new Date().toISOString()
        }]);
        return result;
      } catch (err) {
        console.error('❌ Customer transaction report failed:', err);
        setTestResults(prev => [...prev, {
          test: 'Customer Transaction Report',
          status: 'FAILED',
          error: err,
          timestamp: new Date().toISOString()
        }]);
        throw err;
      }
    },
    enabled: false
  });

  const runTest = () => {
    setTestResults([]);
    refetch();
  };

  const testExport = async (format: 'pdf' | 'excel') => {
    try {
      console.log(`🧪 Testing ${format} export...`);

      // Build params
      const params = new URLSearchParams({ format });
      const filename = format === 'pdf' ? 'test-customer-report.pdf' : 'test-customer-report.xlsx';

      // Make API call
      const response = await fetch(`/api/reports/customer-transactions?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setTestResults(prev => [...prev, {
        test: `${format.toUpperCase()} Export`,
        status: 'SUCCESS',
        timestamp: new Date().toISOString()
      }]);

      console.log(`✅ ${format} export successful`);
    } catch (err) {
      console.error(`❌ ${format} export failed:`, err);
      setTestResults(prev => [...prev, {
        test: `${format.toUpperCase()} Export`,
        status: 'FAILED',
        error: err,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Reports Testing Dashboard</h1>

      <div className="space-y-4 mb-8">
        <button
          onClick={runTest}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Testing...' : '🧪 Test Customer Report API'}
        </button>

        <button
          onClick={() => testExport('pdf')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          📄 Test PDF Export
        </button>

        <button
          onClick={() => testExport('excel')}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          📊 Test Excel Export
        </button>
      </div>

      {/* Data Display */}
      {data && (
        <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded">
          <h3 className="text-lg font-semibold text-green-800 mb-2">API Data:</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Invoices</div>
              <div className="text-xl font-bold text-blue-600">{data.summary?.totalInvoices || 0}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Revenue</div>
              <div className="text-xl font-bold text-green-600">${data.summary?.totalRevenue || 0}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Payments Received</div>
              <div className="text-xl font-bold text-purple-600">${data.summary?.paymentsReceived || 0}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Outstanding</div>
              <div className="text-xl font-bold text-red-600">${data.summary?.outstanding || 0}</div>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Transactions: {data.transactions?.length || 0}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error:</h3>
          <pre className="text-sm text-red-600">{JSON.stringify(error, null, 2)}</pre>
        </div>
      )}

      {/* Test Results */}
      {testResults.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Test Results:</h3>
          <div className="space-y-2">
            {testResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded border ${
                  result.status === 'SUCCESS'
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{result.test}</div>
                    <div className="text-sm opacity-75">{result.timestamp}</div>
                  </div>
                  <div className={`font-bold ${
                    result.status === 'SUCCESS' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {result.status}
                  </div>
                </div>
                {result.error && (
                  <div className="mt-2 text-sm">
                    <pre>{JSON.stringify(result.error, null, 2)}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};